import { Card, makeStyles, Typography } from "@material-ui/core";
import clsx from "clsx";
import _ from "lodash";
import React, { Fragment, useEffect, useState } from "react";
import { t } from "ttag";
import alertSeverities from "../../constants/alertSeverities";
import { useStoreActions, useStoreState } from "../../models/RootStore";
import LightTooltip from "@components/Tooltip/LightTooltip";

const useStyles = makeStyles((theme) => ({
  alerts: {
    display: "flex",
    alignItems: "flex-start",
    flexDirection: "column",
    // maxHeight: '35%'
  },

  alertsTitle: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "0 0 1rem 0",
    fontSize: "33px",
    color: "#fff"
  },

  alertIcon: {
    paddingRight: "0.5rem"
  },

  alertsHeaderTitle: {
    minWidth: "200px",
    fontSize: "20px",
    fontFamily: "Roboto",
    fontWeight: 500,
    color: "#4B1C46",
    paddingRight: "10%"
  },

  alertsStatistics: {
    display: "flex",
    width: "100%",
    overflowX: "auto",
    overflowY: "hidden",
    justifyContent: "space-between"
  },

  singleAlertStatisticsBlock: {
    display: "flex",
    alignItems: "center",
    marginRight: "8px",
    width: "100%",
    opacity: 0.8,
    borderRadius: "10px",
    flexDirection: "row",
    backgroundColor: "#f6f6f7",
    height: 47,
    cursor: "pointer",
    "&:last-child": {
      border: "none",
      marginRight: "0",
    }
  },
  iconStyle: {
    color: "#aaa2aa",
    fontSize: 16,
    marginBottom: "-4px "
  },
  hideIcon: {
    visibility: "hidden"
  },
  singleAlertStatisticsText: {
    fontSize: "14px",
    fontFamily: "Roboto",
    fontWeight: 400,
    color: "rgb(41, 19, 46)",
    whiteSpace: "nowrap"
  },

  singleAlertStatisticsCount: {
    fontSize: "32px",
    fontFamily: "RobotoLight",
    paddingRight: "5px",
    width: "max-content",
    lineHeight: "38px"
  },
  selectedStyle: {
    backgroundColor: "#e9e7e9"
  },
  alertFiltersHolder: {
    display: "flex",
    alignItems: "baseline",
    padding: '5px',
  },
  tooltipTitle: {
    fontFamily: "Roboto",
    fontWeight: 500
  },
}));

const AlertsBand = (props: any) => {
  const { setAlertFilterType, filterType, } = props;
  const classes = useStyles();
  const selections = useStoreState((state) => state.selections.selections);
  const { customerId, siteId } = selections;
  const getSiteAlertCount = useStoreActions((actions) => actions.sites.getSiteAlertCount);
  const getCustomerAlertCount = useStoreActions((actions) => actions.customers.getCustomerAlertCount)
  const userPref = useStoreState((s) => s.users.userPreferences);


  const types = useStoreState((state) => state.types);
  const { eventCategories } = types;

  const [alertCounts, setAlertCounts] = useState({
    anomaly: 0,
    connect: 0,
    maintenance: 0,
    indoorError: 0,
    systemError: 0,
  });

  // Define the expected keys explicitly
  type AlertCountKeys = keyof typeof alertCounts;

  useEffect(() => {
    const fetchAlertCounts = async () => {
      try {
        let res;
        if (siteId) {
          res = await getSiteAlertCount(siteId);
        } else if (customerId) {
          res = await getCustomerAlertCount(customerId);
        } else {
          return;
        }
        const updatedCounts = {
          anomaly: 0,
          connect: 0,
          maintenance: 0,
          indoorError: 0,
          systemError: 0,
        };
        res.forEach((element: { category: number; count: number }) => {
          const key = eventCategories[element.category] as AlertCountKeys;
          if (key in updatedCounts) {
            updatedCounts[key] = element.count;
          }
        });
        setAlertCounts(updatedCounts);
      } catch (error) {
        console.error("Error fetching alert counts:", error);
      }
    };

    if (siteId || customerId) {
      fetchAlertCounts();
    }
  }, [siteId, customerId, userPref]);


  const alertMapping: any = {
    systemErrors: {
      id: "SYSTEM_ERROR",
      text: t`System Error`, //alertSeverities.SYSTEM_ERROR.name,
      tooltipTitle: t`Manufacturer generated Error`,
      tooltipDescription: t`Usually relates to a system level or condenser issue. Has also  an error code from the manufacturer.`,
      color: alertSeverities.SYSTEM_ERROR.color,
      count: alertCounts.systemError
    },
    indoorErrors: {
      id: "INDOOR_ERROR",
      text: t`Indoor Error`,
      tooltipTitle: t`Manufacturer generated Error`,
      tooltipDescription: t`Related to Indoor Unit. Has an error code from the manufacturer`,
      color: alertSeverities.INDOOR_ERROR.color,
      count: alertCounts.indoorError
    },
    notConnected: {
      id: "NOT_CONNECTED",
      text: t`Communication`,
      tooltipTitle: t`Alerts related to connectivity loss`,
      tooltipDescription: t`IDU/s issue -Loss of communication with one/ more) unit/s (e.g. no power to IDU). Communication issue - no connection with cloud device (e.g. no Internet connection, problematic device)`,
      color: alertSeverities.NOT_CONNECTED.color,
      count: alertCounts.connect
    },
    anomalies: {
      id: "ANOMALIES",
      text: t`Anomalies`,
      tooltipTitle: t`Alerts related to an Anomaly Engine`,
      tooltipDescription: t`Anomaly rule condition have  triggered the alert`,
      color: alertSeverities.ANOMALIES.color,
      count: alertCounts.anomaly
    },
    maintenance: {
      id: "MAINTENANCE",
      text: t`Maintenance`,
      tooltipTitle: t`Alerts related to System Maintenance Activity`,
      tooltipDescription: t`For example Filter Clean.`,
      color: alertSeverities.MAINTENANCE.color,
      count: alertCounts.maintenance
    }
  };

  return (
    <Card elevation={0} className={classes.alerts}>
      <div className={classes.alertsTitle}>
        <Typography className={classes.alertsHeaderTitle}>{t`Active Alerts`}</Typography>
      </div>
      <div className={classes.alertsStatistics}>
        {_.map(alertMapping, (alertType, typeKey) => {
          return (
            <Fragment key={typeKey}>
              <div className={clsx(classes.singleAlertStatisticsBlock)}
                onClick={() => setAlertFilterType(filterType === alertType.id ? null : alertType.id)}
              >
                <LightTooltip title={
                  <>
                    <Typography className={classes.tooltipTitle}>{`${alertType.tooltipTitle}`}</Typography>
                    {alertType.tooltipDescription}
                  </>
                }>
                  <div className={classes.alertFiltersHolder}>
                    <Typography
                      className={classes.singleAlertStatisticsCount}
                      style={{ color: alertType.color }}
                    >
                      {alertType.count < 10 ? "0" + alertType.count : alertType.count}
                    </Typography>
                    <span className={classes.singleAlertStatisticsText}>{alertType.text}</span>
                  </div>
                </LightTooltip>
              </div>
            </Fragment>
          );
        })}
      </div>
    </Card>
  );
};

export default AlertsBand;
